/**
 * The wall plan's magnets: the floor plan's smart snapping (`core/geometry/snapping.ts`) turned to
 * face the wall. A thing dragged along the face lines its edges or centre up with the edges and
 * centres of whatever else the plan shows, settles midway between its neighbours in the same row
 * (the wall's ends count as neighbours) or in the wall's middle, and continues an even row; one that
 * also moves up and down does the same with heights, the floor and the ceiling closing its column.
 * The pull reaches 10 px on screen, the target caught last keeps its hold against an equally near
 * rival, and Ctrl/Cmd turns the magnets off, as on the plan.
 */
import type { Item, Level, Wall } from '../core/hp/types';
import { itemFootprint, type Footprints } from '../core/geometry/footprint';
import { verticalExtent } from '../core/geometry/stacking';
import { alongWall, itemFacesWall, wallPlanHeight, type WallPlanSpan, type WallViewTarget } from './wallPlan';

/** `t` runs along the wall, `h` up from the floor. */
export type WallPlanAxis = 't' | 'h';
/** Something on the face a magnet can hold: `item:<id>` or `opening:<id>`. */
export interface WallPlanBox extends WallPlanSpan { id: string }
export interface WallPlanPoint { t: number; h: number }
/** An alignment line, or a distance that equals its twin (`length` set), in face metres. */
export interface WallPlanGuide { kind: 'alignment' | 'equal-gap'; from: WallPlanPoint; to: WallPlanPoint; length?: number }
export interface WallPlanCapture { axis: WallPlanAxis; target: string }
export interface WallPlanSnapResult { dt: number; dh: number; guides: WallPlanGuide[]; captures: WallPlanCapture[] }
/** What the overlay draws over the wall plan besides the dimensions (`Viewer.wallPlanMarks`). */
export interface WallPlanMarks {
  /** The selected item's rectangle; `conflict` while a move drives it into another. */
  selected: (WallPlanSpan & { conflict: boolean }) | null;
  /** What that move drives it into. */
  conflicts: WallPlanSpan[];
  /** The magnets holding whatever is being dragged. */
  guides: WallPlanGuide[];
}
export interface WallPlanSnapOptions {
  /** Metres per CSS pixel. */
  scale: number;
  /** Whether the box also moves up and down. */
  vertical: boolean;
  disabled?: boolean;
  /** What held the box last frame. */
  previous?: readonly WallPlanCapture[];
  /** A shift the wall refuses — past an end, over another opening — is no candidate. */
  fits?: (axis: WallPlanAxis, delta: number) => boolean;
}

/** The pull, in screen pixels. */
export const WALL_PLAN_SNAP_PX = 10;
/** Two ranges share a row when they overlap by more than this. */
const ROW_OVERLAP = .01;

interface Candidate { axis: WallPlanAxis; delta: number; target: string; priority: number; guides: (box: WallPlanSpan) => WallPlanGuide[] }
const lo = (box: WallPlanSpan, axis: WallPlanAxis) => axis === 't' ? box.t0 : box.h0;
const hi = (box: WallPlanSpan, axis: WallPlanAxis) => axis === 't' ? box.t1 : box.h1;
const mid = (box: WallPlanSpan, axis: WallPlanAxis) => (lo(box, axis) + hi(box, axis)) / 2;
const other = (axis: WallPlanAxis): WallPlanAxis => axis === 't' ? 'h' : 't';
const point = (axis: WallPlanAxis, main: number, cross: number): WallPlanPoint => axis === 't' ? { t: main, h: cross } : { t: cross, h: main };
const overlap = (a: WallPlanSpan, b: WallPlanSpan, axis: WallPlanAxis) => Math.min(hi(a, axis), hi(b, axis)) - Math.max(lo(a, axis), lo(b, axis)) > ROW_OVERLAP;
const shifted = (box: WallPlanSpan, dt: number, dh: number): WallPlanSpan => ({ t0: box.t0 + dt, t1: box.t1 + dt, h0: box.h0 + dh, h1: box.h1 + dh });

/** Where a box dragged to `box` should go; `bounds` is the wall's length and height. */
export function snapWallPlanBox(box: WallPlanSpan, others: readonly WallPlanBox[], bounds: { length: number; height: number }, options: WallPlanSnapOptions): WallPlanSnapResult {
  if (options.disabled) return { dt: 0, dh: 0, guides: [], captures: [] };
  const tolerance = WALL_PLAN_SNAP_PX * options.scale;
  // A switch or a hook does not pull a wardrobe about: neighbours much smaller than the box are ignored.
  const least = Math.min(Math.min(box.t1 - box.t0, box.h1 - box.h0) * .35, .4);
  const neighbours = others.filter(n => Math.min(n.t1 - n.t0, n.h1 - n.h0) >= least).sort((a, b) => a.id.localeCompare(b.id));
  const candidates: Candidate[] = [];
  const add = (candidate: Candidate) => {
    if (Math.abs(candidate.delta) <= tolerance && (options.fits?.(candidate.axis, candidate.delta) ?? true)) candidates.push(candidate);
  };
  for (const axis of options.vertical ? ['t', 'h'] as const : ['t'] as const) {
    const cross = other(axis), limit = axis === 't' ? bounds.length : bounds.height;
    const features = [lo(box, axis), mid(box, axis), hi(box, axis)];
    // Edges and centres in line with a neighbour's.
    for (const n of neighbours) {
      const targets = [lo(n, axis), mid(n, axis), hi(n, axis)];
      for (let a = 0; a < 3; a++) for (let b = 0; b < 3; b++) {
        const value = targets[b];
        add({ axis, delta: value - features[a], target: `${n.id}:${a}:${b}`, priority: a === 1 && b === 1 ? 1 : 2,
          guides: final => [{ kind: 'alignment', from: point(axis, value, Math.min(lo(final, cross), lo(n, cross))), to: point(axis, value, Math.max(hi(final, cross), hi(n, cross))) }] });
      }
    }
    // Along the wall: flush with either end, or centred on the wall.
    if (axis === 't') {
      add({ axis, delta: -features[0], target: 'wall:start', priority: 1, guides: () => [{ kind: 'alignment', from: { t: 0, h: 0 }, to: { t: 0, h: bounds.height } }] });
      add({ axis, delta: limit - features[2], target: 'wall:end', priority: 1, guides: () => [{ kind: 'alignment', from: { t: limit, h: 0 }, to: { t: limit, h: bounds.height } }] });
      add({ axis, delta: limit / 2 - features[1], target: 'wall:middle', priority: 1, guides: final => {
        const at = mid(final, 'h');
        return [{ kind: 'equal-gap', from: { t: 0, h: at }, to: { t: final.t0, h: at }, length: final.t0 },
          { kind: 'equal-gap', from: { t: final.t1, h: at }, to: { t: limit, h: at }, length: limit - final.t1 }];
      } });
    }
    // The row: neighbours overlapping the box across the axis, closed by the wall's ends (or the floor and the ceiling).
    const row = [{ id: axis === 't' ? 'wall:start' : 'floor', lo: 0, hi: 0 },
      ...neighbours.filter(n => overlap(n, box, cross)).map(n => ({ id: n.id, lo: lo(n, axis), hi: hi(n, axis) })),
      { id: axis === 't' ? 'wall:end' : 'ceiling', lo: limit, hi: limit }];
    const centre = features[1], size = features[2] - features[0];
    const before = row.filter(n => (n.lo + n.hi) / 2 < centre).sort((a, b) => a.hi - b.hi || a.lo - b.lo);
    const after = row.filter(n => (n.lo + n.hi) / 2 > centre).sort((a, b) => a.lo - b.lo || a.hi - b.hi);
    const left = before.at(-1), right = after[0];
    const gap = (from: number, to: number, final: WallPlanSpan): WallPlanGuide => {
      const at = mid(final, cross);
      return { kind: 'equal-gap', from: point(axis, from, at), to: point(axis, to, at), length: to - from };
    };
    if (left && right) {
      const space = (right.lo - left.hi - size) / 2;
      if (space > .01) add({ axis, delta: (left.hi + right.lo) / 2 - centre, target: `gap:${left.id}:${right.id}`, priority: 0,
        guides: final => [gap(left.hi, lo(final, axis), final), gap(hi(final, axis), right.lo, final)] });
    }
    // An even row continued: the gap between the two neighbours on one side repeated on this one.
    const farLeft = before.at(-2), farRight = after[1];
    if (left && farLeft) {
      const step = left.lo - farLeft.hi;
      if (step > .01) add({ axis, delta: left.hi + step - features[0], target: `step:${farLeft.id}:${left.id}`, priority: 2,
        guides: final => [gap(farLeft.hi, left.lo, final), gap(left.hi, lo(final, axis), final)] });
    }
    if (right && farRight) {
      const step = farRight.lo - right.hi;
      if (step > .01) add({ axis, delta: right.lo - step - features[2], target: `step:${right.id}:${farRight.id}`, priority: 2,
        guides: final => [gap(hi(final, axis), right.lo, final), gap(right.hi, farRight.lo, final)] });
    }
  }
  const winners = new Map<WallPlanAxis, Candidate>();
  for (const axis of ['t', 'h'] as const) {
    const held = options.previous?.find(capture => capture.axis === axis)?.target;
    const score = (candidate: Candidate) => Math.abs(candidate.delta) - (candidate.target === held ? Math.min(.005, options.scale) : 0);
    const winner = candidates.filter(candidate => candidate.axis === axis)
      .sort((a, b) => score(a) - score(b) || a.priority - b.priority || a.target.localeCompare(b.target))[0];
    if (winner) winners.set(axis, winner);
  }
  const dt = winners.get('t')?.delta ?? 0, dh = winners.get('h')?.delta ?? 0, final = shifted(box, dt, dh);
  return { dt, dh, guides: [...winners.values()].flatMap(winner => winner.guides(final)),
    captures: [...winners.values()].map(winner => ({ axis: winner.axis, target: winner.target })) };
}

/** An item's rectangle on the face: its footprint along the wall, its bottom and top. */
export function itemWallPlanBox(item: Item, wall: Wall, footprints?: Footprints): WallPlanSpan {
  const ts = itemFootprint(item, footprints).map(corner => alongWall(wall, corner));
  const { bottom, top } = verticalExtent(item);
  return { t0: Math.min(...ts), t1: Math.max(...ts), h0: bottom, h1: top };
}

/** Everything the wall plan shows on this face — items against it and its openings — except `exclude` (bare ids). */
export function wallPlanBoxes(level: Pick<Level, 'walls' | 'items' | 'openings'>, target: WallViewTarget, exclude: ReadonlySet<string>, footprints?: Footprints): WallPlanBox[] {
  const wall = level.walls.find(candidate => candidate.id === target.wallId);
  if (!wall) return [];
  const boxes: WallPlanBox[] = [];
  for (const item of level.items) {
    if (exclude.has(item.id) || !itemFacesWall(item, wall, target.side, footprints)) continue;
    boxes.push({ id: `item:${item.id}`, ...itemWallPlanBox(item, wall, footprints) });
  }
  for (const opening of level.openings) {
    if (opening.wallId !== wall.id || exclude.has(opening.id)) continue;
    boxes.push({ id: `opening:${opening.id}`, t0: opening.offset - opening.width / 2, t1: opening.offset + opening.width / 2, h0: opening.sill, h1: opening.sill + opening.height });
  }
  return boxes;
}

/** The wall's extent for the magnets: its length and its height in the plan. */
export const wallPlanBounds = (level: Pick<Level, 'height'>, wall: Wall) => ({ length: wall.length, height: wallPlanHeight(level, wall) });
